/**
 * Инициализация i18next.
 *
 * Импортируется один раз из index.js. Ресурсы подключены статически
 * (пока namespaces мало); при росте числа namespace имеет смысл перейти
 * на ленивую загрузку через i18next-http-backend или динамический import().
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import {
    DEFAULT_NAMESPACE,
    FALLBACK_LANGUAGE,
    LANGUAGE_STORAGE_KEY,
    NAMESPACES,
    SUPPORTED_LANGUAGE_CODES,
    getLanguageDir,
    normalizeLanguage,
} from "./config";

import ruCommon from "./locales/ru/common.json";
import ruMenu from "./locales/ru/menu.json";
import ruLayout from "./locales/ru/layout.json";
import ruUi from "./locales/ru/ui.json";
import ruChat from "./locales/ru/chat.json";
import ruAuth from "./locales/ru/auth.json";
import ruDashboard from "./locales/ru/dashboard.json";
import ruSchedule from "./locales/ru/schedule.json";
import ruStaff from "./locales/ru/staff.json";
import ruWarehouse from "./locales/ru/warehouse.json";
import ruProducts from "./locales/ru/products.json";
import ruContacts from "./locales/ru/contacts.json";
import ruKnowledgeBase from "./locales/ru/knowledge_base.json";
import ruSettings from "./locales/ru/settings.json";
import ruFinance from "./locales/ru/finance.json";
import ruInstructions from "./locales/ru/instructions.json";
import ruTemplates from "./locales/ru/templates.json";
import ruPatients from "./locales/ru/patients.json";
import ruPatient from "./locales/ru/patient.json";
import ruAiAssistant from "./locales/ru/aiAssistant.json";
import enCommon from "./locales/en/common.json";
import enMenu from "./locales/en/menu.json";
import enLayout from "./locales/en/layout.json";
import enUi from "./locales/en/ui.json";
import enChat from "./locales/en/chat.json";
import enAuth from "./locales/en/auth.json";
import enDashboard from "./locales/en/dashboard.json";
import enSchedule from "./locales/en/schedule.json";
import enStaff from "./locales/en/staff.json";
import enWarehouse from "./locales/en/warehouse.json";
import enProducts from "./locales/en/products.json";
import enContacts from "./locales/en/contacts.json";
import enKnowledgeBase from "./locales/en/knowledge_base.json";
import enSettings from "./locales/en/settings.json";
import enFinance from "./locales/en/finance.json";
import enInstructions from "./locales/en/instructions.json";
import enTemplates from "./locales/en/templates.json";
import enPatients from "./locales/en/patients.json";
import enPatient from "./locales/en/patient.json";
import enAiAssistant from "./locales/en/aiAssistant.json";
import arCommon from "./locales/ar/common.json";
import arMenu from "./locales/ar/menu.json";
import arLayout from "./locales/ar/layout.json";
import arUi from "./locales/ar/ui.json";
import arChat from "./locales/ar/chat.json";
import arAuth from "./locales/ar/auth.json";
import arDashboard from "./locales/ar/dashboard.json";
import arSchedule from "./locales/ar/schedule.json";
import arStaff from "./locales/ar/staff.json";
import arWarehouse from "./locales/ar/warehouse.json";
import arProducts from "./locales/ar/products.json";
import arContacts from "./locales/ar/contacts.json";
import arKnowledgeBase from "./locales/ar/knowledge_base.json";
import arSettings from "./locales/ar/settings.json";
import arFinance from "./locales/ar/finance.json";
import arInstructions from "./locales/ar/instructions.json";
import arTemplates from "./locales/ar/templates.json";
import arPatients from "./locales/ar/patients.json";
import arPatient from "./locales/ar/patient.json";

const resources = {
    ru: {
        common: ruCommon,
        menu: ruMenu,
        layout: ruLayout,
        ui: ruUi,
        chat: ruChat,
        auth: ruAuth,
        dashboard: ruDashboard,
        schedule: ruSchedule,
        staff: ruStaff,
        warehouse: ruWarehouse,
        products: ruProducts,
        contacts: ruContacts,
        knowledge_base: ruKnowledgeBase,
        settings: ruSettings,
        finance: ruFinance,
        instructions: ruInstructions,
        templates: ruTemplates,
        patients: ruPatients,
        patient: ruPatient,
        aiAssistant: ruAiAssistant,
    },
    en: {
        common: enCommon,
        menu: enMenu,
        layout: enLayout,
        ui: enUi,
        chat: enChat,
        auth: enAuth,
        dashboard: enDashboard,
        schedule: enSchedule,
        staff: enStaff,
        warehouse: enWarehouse,
        products: enProducts,
        contacts: enContacts,
        knowledge_base: enKnowledgeBase,
        settings: enSettings,
        finance: enFinance,
        instructions: enInstructions,
        templates: enTemplates,
        patients: enPatients,
        patient: enPatient,
        aiAssistant: enAiAssistant,
    },
    ar: {
        common: arCommon,
        menu: arMenu,
        layout: arLayout,
        ui: arUi,
        chat: arChat,
        auth: arAuth,
        dashboard: arDashboard,
        schedule: arSchedule,
        staff: arStaff,
        warehouse: arWarehouse,
        products: arProducts,
        contacts: arContacts,
        knowledge_base: arKnowledgeBase,
        settings: arSettings,
        finance: arFinance,
        instructions: arInstructions,
        templates: arTemplates,
        patients: arPatients,
        patient: arPatient,
    },
};

/** Проставляет lang/dir на <html> — это и включает RTL-раскладку. */
export function applyDocumentLanguage(lng) {
    const code = normalizeLanguage(lng);
    const root = document.documentElement;
    root.setAttribute("lang", code);
    root.setAttribute("dir", getLanguageDir(code));
}

i18n.use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources,
        fallbackLng: FALLBACK_LANGUAGE,
        supportedLngs: SUPPORTED_LANGUAGE_CODES,
        // "en-US" -> "en" без создания отдельной локали.
        nonExplicitSupportedLngs: true,
        load: "currentOnly",
        defaultNS: DEFAULT_NAMESPACE,
        ns: NAMESPACES,
        interpolation: {
            // React сам экранирует значения — двойное экранирование не нужно.
            escapeValue: false,
        },
        detection: {
            // Язык браузера намеренно не учитываем: приложение всегда стартует
            // на fallback (ru), пока пользователь сам не выберет язык в UI.
            order: ["localStorage"],
            lookupLocalStorage: LANGUAGE_STORAGE_KEY,
            caches: ["localStorage"],
            // Страховка: любые сохранённые коды нормализуем к поддерживаемым.
            convertDetectedLanguage: (lng) => normalizeLanguage(lng),
        },
        react: {
            // Ресурсы подключены синхронно — Suspense не нужен.
            useSuspense: false,
        },
        returnNull: false,
    });

i18n.on("languageChanged", applyDocumentLanguage);
applyDocumentLanguage(i18n.resolvedLanguage || i18n.language);

export default i18n;
